import { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";

export function HeroBackdrop() {
  const reduced = useReducedMotion();
  const glowRef = useRef<HTMLDivElement>(null);
  const flowRef = useRef<HTMLDivElement>(null);
  const mouse = useRef({ x: 0, y: 0 });
  const frame = useRef<number | null>(null);

  useEffect(() => {
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    const desktop = window.matchMedia("(min-width: 1024px)").matches;
    if (reduced || !finePointer || !desktop) return;

    let busy = false;
    function apply() {
      busy = false;
      const nx = mouse.current.x / window.innerWidth - 0.5;
      const ny = mouse.current.y / window.innerHeight - 0.5;
      if (glowRef.current) glowRef.current.style.transform = `translate3d(${(nx * 8).toFixed(2)}px, ${(ny * 6).toFixed(2)}px, 0)`;
      if (flowRef.current) flowRef.current.style.transform = `translate3d(${(nx * 10).toFixed(2)}px, ${(ny * 7).toFixed(2)}px, 0)`;
    }
    function onMove(e: PointerEvent) {
      mouse.current = { x: e.clientX, y: e.clientY };
      if (!busy) {
        busy = true;
        frame.current = requestAnimationFrame(apply);
      }
    }
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [reduced]);

  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      {/* atmospheric base wash (mood only, no competing detail) */}
      <div className="absolute inset-0 bg-[radial-gradient(90%_70%_at_80%_28%,rgba(120,180,205,0.16),transparent_62%),radial-gradient(70%_60%_at_90%_72%,rgba(150,205,190,0.14),transparent_56%)]" />

      {/* soft brand glow */}
      <div
        ref={glowRef}
        className="absolute -inset-3 z-[1] will-change-transform"
        style={{
          backgroundImage:
            "radial-gradient(circle at 66% 44%, rgba(36,87,245,0.09), transparent 36%), radial-gradient(circle at 84% 62%, rgba(40,201,139,0.08), transparent 34%)"
        }}
      />

      {/* very thin blue→cyan→green transition paths (lower-left, clear of the diagram) */}
      <div ref={flowRef} className="absolute -inset-3 z-[1] will-change-transform">
        <svg viewBox="0 0 1800 1000" preserveAspectRatio="none" className="h-full w-full">
          <defs>
            <linearGradient id="heroFlowGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#2457f5" />
              <stop offset="50%" stopColor="#12afcb" />
              <stop offset="100%" stopColor="#28c98b" />
            </linearGradient>
          </defs>
          <path d="M40 760 C 300 700, 560 720, 860 640" fill="none" stroke="url(#heroFlowGrad)" strokeWidth="2" strokeLinecap="round" opacity="0.18" />
          <path d="M60 834 C 340 782, 620 802, 900 744" fill="none" stroke="url(#heroFlowGrad)" strokeWidth="2" strokeLinecap="round" opacity="0.14" />
        </svg>
      </div>

      {/* gentle light veil on the left so the headline stays crisp */}
      <div className="absolute inset-0 z-[2] bg-[linear-gradient(90deg,rgba(246,243,237,0.55)_0%,rgba(246,243,237,0.2)_45%,transparent_72%)]" />
    </div>
  );
}
